/**
 * Footer component.
 * @module components/theme/Footer/Footer
 */

import React from 'react';
import { Segment } from 'semantic-ui-react';
import { injectIntl, FormattedMessage, useIntl } from 'react-intl';
import { useSelector, shallowEqual } from 'react-redux';
import { messages } from '../../../../messages';

/**
 * Component to display the footer.
 * @function Footer
 * @param {Object} intl Intl object
 * @returns {string} Markup of the component
 */
const Footer = ({ intl }) => {
  const { siteActions = [] } = useSelector(
    (state) => ({
      siteActions: state.actions?.actions?.site_actions,
    }),
    shallowEqual,
  );

  const localization = useIntl();
  const imprintHref =
    localization.locale === 'de' ? '/de/impressum' : '/en/imprint';
  const dataPrivacyHref =
    localization.locale === 'de' ? '/de/datenschutz' : '/en/data-privacy';
  const solutionsHref =
    localization.locale === 'de'
      ? '/de/loesungen-produkte'
      : '/en/solutions-products';
  const whywithusHref =
    localization.locale === 'de'
      ? '/de/#scroll-section'
      : '/en/#scroll-section';
  const referencesHref =
    localization.locale === 'de' ? '/de/referenzen' : '/en/references';
  const aboutUsHref =
    localization.locale === 'de' ? '/de/ueber-uns' : '/en/about-us';
  const researchHref =
    localization.locale === 'de' ? '/de/forschung' : '/en/research';
  const blogHref = localization.locale === 'de' ? '/de/blog' : '/en/blog';
  const karrieremituns =
    localization.locale === 'de'
      ? '/de/karriere-bei-x-net/karriere'
      : '/en/career-at-x-net/career';
  const careerHref =
    localization.locale === 'de'
      ? '/de/karriere-bei-x-net/karriere'
      : '/en/career-at-x-net/career';
  const teamHref =
    localization.locale === 'de'
      ? '/de/ueber-unser-team/ueber-uns#wrapper-geschaeftsfuehrung'
      : '/en/about-our-team/about-us#wrapper-geschaeftsfuehrung';
  const uptodate =
    localization.locale === 'de'
      ? '/de/wissenswertes-aus-unserem-blog'
      : '/en/good-to-know-from-our-blog';
  const contact =
    localization.locale === 'de' ? '/de/impressum' : '/en/imprint';
  const vdpHref = localization.locale === 'de' ? '/de/vdp' : '/en/vdp';

  const mailtoSubject = intl.formatMessage(messages.mailto_subject);
  const mailtoBody = intl.formatMessage(messages.mailto_body);
  const mailtoLink = `mailto:office@x-net.at?subject=${mailtoSubject}&body=${mailtoBody}`;

  return (
    <Segment
      role="contentinfo"
      vertical
      padded
      inverted
      color="grey"
      textAlign="center"
      id="footer"
      aria-label="Footer"
      tabIndex="-1"
    >
      <div className="footer-newsletter">
        <div className="inner-newsletter">
          <a
            href={mailtoLink}
            rel="noopener noreferrer"
            className="button-link"
          >
            <FormattedMessage
              id="x_newsletter"
              defaultMessage="Click here to subscribe to our newsletter"
            />
          </a>
          <a href={mailtoLink} className="button">
            <FormattedMessage
              id="x_newsletter_sign_up"
              defaultMessage="Sign up"
            />
          </a>
        </div>
        <div className="outer-newsletter">
          <a href={mailtoLink} className="button-mobile">
            <FormattedMessage
              id="x_newsletter_sign_up"
              defaultMessage="Sign up"
            />
          </a>
        </div>
      </div>
      <div className="footer-outer">
        <div className="footer-inner">
          <div className="footer-button">
            <a
              href="#"
              onClick={(event) => {
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
                event.preventDefault();
              }}
            >
              <FormattedMessage id="back_to_top" defaultMessage="Back to top" />
            </a>
          </div>
          <div className="footer">
            <div className="footer-pack-right">
              <div className="footer-column footer-column-start">
                <a href={whywithusHref}>
                  <h3>
                    <FormattedMessage
                      id="why_with_us"
                      defaultMessage="Why with us"
                    />
                  </h3>
                </a>
                <a href={solutionsHref}>
                  <p>
                    <FormattedMessage
                      id="solutions_products"
                      defaultMessage="Solutions & Products"
                    />
                  </p>
                </a>
                <a href={referencesHref}>
                  <p>
                    <FormattedMessage
                      id="references"
                      defaultMessage="References"
                    />
                  </p>
                </a>
                <a href={aboutUsHref}>
                  <p>
                    <FormattedMessage id="about_us" defaultMessage="About us" />
                  </p>
                </a>
                <a href={researchHref}>
                  <p>
                    <FormattedMessage id="research" defaultMessage="Research" />
                  </p>
                </a>
                <a href={blogHref}>
                  <p>
                    <FormattedMessage id="blog" defaultMessage="Blog" />
                  </p>
                </a>
              </div>
              <div className="footer-column">
                <a href={karrieremituns}>
                  <h3>
                    <FormattedMessage
                      id="career_with_us"
                      defaultMessage="Join our team"
                    />
                  </h3>
                </a>
                <a href={careerHref}>
                  <p>
                    <FormattedMessage
                      id="free_jobs"
                      defaultMessage="Available jobs"
                    />
                  </p>
                </a>
                {/*<p>Bewerbungsprozess</p>*/}
                {/*<p>FAQs</p>*/}
                <a href={teamHref}>
                  <p>
                    <FormattedMessage id="team" defaultMessage="Team" />
                  </p>
                </a>
              </div>
            </div>
            <div className="footer-pack-left">
              <div className="footer-column social-media">
                <a href={uptodate}>
                  <h3>
                    <FormattedMessage
                      id="up_to_date"
                      defaultMessage="Stay up to date"
                    />
                  </h3>
                </a>
                <div className="footer-up-to-date">
                  <a
                    href="https://www.facebook.com/XNetServicesGmbh/?locale=de_DE"
                    target="_blank"
                    title="Facebook"
                    rel="noreferrer"
                  >
                    <div className="social-media-icons">
                      <svg
                        width="200"
                        zoomAndPan="magnify"
                        viewBox="0 0 150 149.41406"
                        preserveAspectRatio="xMidYMid"
                        version="1.0"
                        id="svg12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g id="g14">
                          <path
                            d="m 193.35156,12.070312 h 150 V 162 h -150 z m 0,0"
                            clipRule="nonzero"
                            id="path13"
                          />
                        </g>
                        <path
                          d="M 324.49609,12.363281 H 212.16016 c -10.32422,0 -18.8086,8.472657 -18.8086,18.75 V 142.93359 c 0,10.27735 8.48438,18.84375 18.8086,18.84375 h 55.89843 v -51.5625 H 249.60156 V 87.363281 h 18.45703 V 68.386719 c 0,-15.441407 12.84375,-27.605469 28.35547,-27.605469 h 19.10547 v 23.144531 h -9.74609 c -5.16407,0 -9.29688,-0.683593 -9.29688,4.460938 v 18.976562 h 23.33985 l -9.35938,22.851559 h -13.98047 v 51.5625 h 28.01953 c 10.32813,0 18.85547,-8.5664 18.85547,-18.84375 V 31.113281 c 0,-10.277343 -8.52734,-18.75 -18.85547,-18.75 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path14"
                          clipPath="url(#clipPath13)"
                          transform="translate(-193.35156,-12.363256)"
                        />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://www.instagram.com/xnet_services/"
                    target="_blank"
                    title="Instagram"
                    rel="noreferrer"
                  >
                    <div className="social-media-icons">
                      <svg
                        width="199.47398"
                        viewBox="0 0 149.60549 149.99998"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g
                          clipPath="url(#ee0fdfc34a)"
                          id="g4"
                          transform="translate(-11.281249,-12.68362)"
                        >
                          <path
                            d="M 124.67969,162.68359 H 47.886719 c -20.292969,0 -36.605469,-16.3125 -36.605469,-36.60546 V 49.289062 c 0,-20.292968 16.3125,-36.605468 36.605469,-36.605468 h 76.394531 c 20.28906,0 36.60547,16.3125 36.60547,36.605468 v 76.394528 c 0,20.28907 -16.31641,36.60157 -36.60547,36.60157 z M 47.886719,24.621094 c -13.527344,0 -24.667969,11.140625 -24.667969,24.667968 v 76.394528 c 0,13.52735 11.140625,24.66797 24.667969,24.66797 h 76.394531 c 13.52734,0 24.66797,-11.14062 24.66797,-24.66797 V 49.289062 c 0,-13.527343 -11.14063,-24.667968 -24.66797,-24.667968 z m 0,0"
                            fillOpacity="1"
                            fillRule="nonzero"
                            id="path4"
                          />
                        </g>
                        <path
                          d="m 75.199221,123.34373 c -26.25782,0 -47.34766,-21.08985 -47.34766,-47.34766 0,-26.26172 21.08984,-47.34766 47.34766,-47.34766 26.261709,0 47.347649,21.08594 47.347649,47.34766 0,26.25781 -21.08594,47.34766 -47.347649,47.34766 z m 0,-83.15625 c -19.89454,0 -35.8086,15.91406 -35.8086,35.80859 0,19.89453 15.91406,35.80859 35.8086,35.80859 19.89453,0 35.808589,-15.91406 35.808589,-35.80859 0,-19.89453 -15.914059,-35.80859 -35.808589,-35.80859 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path5"
                        />
                        <path
                          d="m 75.199221,97.48044 c -11.9336,0 -21.48438,-9.54687 -21.48438,-21.48437 0,-11.9375 9.55078,-21.48438 21.48438,-21.48438 11.9375,0 21.48828,9.54688 21.48828,21.48438 0,11.9375 -9.55078,21.48437 -21.48828,21.48437 z m 0,-31.03515 c -5.17188,0 -9.54688,4.3789 -9.54688,9.55078 0,5.17187 4.375,9.54687 9.54688,9.54687 5.17187,0 9.55078,-4.375 9.55078,-9.54687 0,-5.17188 -4.37891,-9.55078 -9.55078,-9.55078 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path6"
                        />
                        <path
                          d="m 126.52734,31.43357 c 0,0.52344 -0.0508,1.03906 -0.15234,1.55078 -0.10157,0.51563 -0.25391,1.01172 -0.45313,1.49609 -0.19922,0.48047 -0.44531,0.94141 -0.73828,1.375 -0.28906,0.4336 -0.61719,0.83594 -0.98828,1.20313 -0.36719,0.37109 -0.76953,0.69922 -1.20703,0.99219 -0.4336,0.28906 -0.89063,0.53515 -1.375,0.73437 -0.48047,0.19922 -0.98047,0.35156 -1.49219,0.45313 -0.51172,0.10156 -1.03125,0.15234 -1.55078,0.15234 -0.52344,0 -1.04297,-0.0508 -1.55469,-0.15234 -0.51172,-0.10157 -1.00781,-0.25391 -1.49219,-0.45313 -0.48046,-0.19922 -0.9414,-0.44531 -1.375,-0.73437 -0.43359,-0.29297 -0.83593,-0.6211 -1.20703,-0.99219 -0.36718,-0.36719 -0.69922,-0.76953 -0.98828,-1.20313 -0.28906,-0.43359 -0.53515,-0.89453 -0.73437,-1.375 -0.20313,-0.48437 -0.35157,-0.98046 -0.45313,-1.49609 -0.10156,-0.51172 -0.15234,-1.02734 -0.15234,-1.55078 0,-0.52344 0.0508,-1.03906 0.15234,-1.55078 0.10156,-0.51563 0.25,-1.01172 0.45313,-1.4961 0.19922,-0.48046 0.44531,-0.9414 0.73437,-1.375 0.28906,-0.43359 0.6211,-0.83593 0.98828,-1.20312 0.3711,-0.37109 0.77344,-0.70313 1.20703,-0.99219 0.4336,-0.28906 0.89454,-0.53515 1.375,-0.73437 0.48438,-0.19922 0.98047,-0.35157 1.49219,-0.45313 0.51172,-0.10156 1.03125,-0.15234 1.55469,-0.15234 0.51953,0 1.03906,0.0508 1.55078,0.15234 0.51172,0.10156 1.01172,0.25391 1.49219,0.45313 0.48437,0.19922 0.9414,0.44531 1.375,0.73437 0.4375,0.28906 0.83984,0.6211 1.20703,0.99219 0.37109,0.36719 0.69922,0.76953 0.98828,1.20312 0.29297,0.4336 0.53906,0.89454 0.73828,1.375 0.19922,0.48438 0.35156,0.98047 0.45313,1.4961 0.10156,0.51172 0.15234,1.02734 0.15234,1.55078 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path7"
                        />
                      </svg>
                    </div>
                  </a>
                  <a
                    href="https://at.linkedin.com/company/x-net-services"
                    target="_blank"
                    title="LinkedIn"
                    rel="noreferrer"
                  >
                    <div className="social-media-icons">
                      <svg
                        width="199.99998"
                        zoomAndPan="magnify"
                        viewBox="0 0 149.99999 149.99998"
                        preserveAspectRatio="xMidYMid"
                        version="1.0"
                        id="svg12"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="m 34.968738,54.40233 h 11.09375 c 0.28515,0 0.55469,0.0508 0.8164,0.16015 0.26172,0.10938 0.48829,0.26172 0.69141,0.46094 0.19922,0.19922 0.35156,0.42969 0.46094,0.69141 0.10547,0.25781 0.16015,0.53125 0.16015,0.8125 v 61.02343 c 0,0.28516 -0.0547,0.55469 -0.16015,0.81641 -0.10938,0.26172 -0.26172,0.49219 -0.46094,0.69141 -0.20312,0.19922 -0.42969,0.35156 -0.69141,0.46093 -0.26171,0.10547 -0.53125,0.16016 -0.8164,0.16016 h -11.09375 c -0.28125,0 -0.55469,-0.0547 -0.8125,-0.16016 -0.26172,-0.10937 -0.49219,-0.26171 -0.69141,-0.46093 -0.19922,-0.19922 -0.35156,-0.42969 -0.46094,-0.69141 -0.10937,-0.26172 -0.16406,-0.53125 -0.16406,-0.81641 V 56.52733 c 0,-0.28125 0.0547,-0.55469 0.16406,-0.8125 0.10938,-0.26172 0.26172,-0.49219 0.46094,-0.69141 0.19922,-0.19922 0.42969,-0.35156 0.69141,-0.46094 0.25781,-0.10937 0.53125,-0.16015 0.8125,-0.16015 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path9"
                        />
                        <path
                          d="m 49.453108,39.34373 c -0.0156,-5.23047 -3.76953,-9.02343 -8.9375,-9.02343 -5.16406,0 -9.0625,3.89453 -9.0625,9.0625 0,2.40234 0.87891,4.64062 2.46875,6.29687 1.63672,1.69922 3.89063,2.63672 6.34375,2.63672 2.71875,0 5.14453,-0.95703 6.83203,-2.70703 1.58204,-1.63672 2.41797,-3.84375 2.35547,-6.22656 0,-0.0117 0,-0.0234 0,-0.0391 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path10"
                        />
                        <path
                          d="M 110.22655,58.69139 C 104.69921,53.74998 98.226548,53.0117 94.859358,53.0117 c -5.03515,0 -9.82812,1.34766 -13.87109,3.89063 -1.76563,1.11328 -3.35938,2.42968 -4.78516,3.95703 l -0.28125,-4.46875 c -0.0156,-0.26953 -0.082,-0.52735 -0.19531,-0.77344 -0.11719,-0.24609 -0.27344,-0.46094 -0.47266,-0.64453 -0.19531,-0.1875 -0.42187,-0.32813 -0.67187,-0.42969 -0.25391,-0.0977 -0.51563,-0.14844 -0.78516,-0.14844 h -9.82812 c -0.29688,0 -0.58203,0.0625 -0.85156,0.17969 -0.27344,0.11719 -0.50782,0.28516 -0.71094,0.50391 -0.19922,0.21484 -0.34766,0.46484 -0.44531,0.74219 -0.0977,0.28125 -0.13672,0.5664 -0.11329,0.86328 0.34766,4.65625 0.4961,9.55078 0.4961,16.35547 v 44.51171 c 0,0.28516 0.0547,0.55469 0.16015,0.81641 0.10938,0.26172 0.26172,0.49219 0.46485,0.69141 0.19922,0.19922 0.42969,0.35156 0.6875,0.46093 0.26172,0.10547 0.53125,0.16016 0.8164,0.16016 h 11.09375 c 0.28125,0 0.55469,-0.0547 0.81641,-0.16016 0.25781,-0.10937 0.48828,-0.26171 0.6875,-0.46093 0.19922,-0.19922 0.35547,-0.42969 0.46094,-0.69141 0.10937,-0.26172 0.16406,-0.53125 0.16406,-0.81641 v -36.6875 c 0,-1.72656 0.22656,-3.34375 0.62891,-4.44531 0.0117,-0.0312 0.0234,-0.0664 0.0351,-0.10547 1.5,-4.89453 6.05078,-9.84375 12.84375,-9.84375 4.20313,0 7.21875,1.4375 9.222662,4.39453 1.80859,2.67188 2.76562,6.65235 2.76562,11.50782 v 35.17968 c 0,0.28516 0.0547,0.55469 0.16407,0.81641 0.10547,0.26172 0.26172,0.49219 0.46093,0.69141 0.19922,0.19922 0.42969,0.35156 0.6875,0.46093 0.26172,0.10547 0.53516,0.16016 0.81641,0.16016 H 116.414 c 0.28516,0 0.55469,-0.0547 0.81641,-0.16016 0.25781,-0.10937 0.48828,-0.26171 0.6875,-0.46093 0.20312,-0.19922 0.35547,-0.42969 0.46484,-0.69141 0.10547,-0.26172 0.16016,-0.53125 0.16016,-0.81641 v -36.4375 c 0,-5.1914 -0.78516,-9.79296 -2.33203,-13.67578 -1.39063,-3.49218 -3.40235,-6.43359 -5.98438,-8.74609 z m 0,0"
                          fillOpacity="1"
                          fillRule="nonzero"
                          id="path11"
                        />
                        <g
                          clipPath="url(#e897dafda6)"
                          id="g12"
                          transform="translate(-11.699232,-192.4883)"
                        >
                          <path
                            d="M 142.17187,192.48828 H 31.226562 c -10.765624,0 -19.527343,8.76172 -19.527343,19.52734 v 110.94532 c 0,10.76562 8.761719,19.52734 19.527343,19.52734 H 142.17187 c 10.76563,0 19.52735,-8.76172 19.52735,-19.52734 V 212.01562 c 0,-10.76562 -8.76172,-19.52734 -19.52735,-19.52734 z m 5.81641,20.47266 v 109.05468 c 0,0.44141 -0.043,0.88282 -0.13281,1.31641 -0.0859,0.4375 -0.21485,0.85938 -0.38281,1.26953 -0.17188,0.41016 -0.37891,0.79688 -0.625,1.16797 -0.25,0.36719 -0.52735,0.71094 -0.84375,1.02344 -0.3125,0.3125 -0.65235,0.59375 -1.02344,0.83984 -0.36719,0.2461 -0.75781,0.45703 -1.16797,0.625 -0.41016,0.17188 -0.83203,0.30078 -1.26953,0.38672 -0.4336,0.0859 -0.8711,0.13281 -1.31641,0.13281 H 32.171875 c -0.441406,0 -0.882813,-0.0469 -1.316406,-0.13281 -0.4375,-0.0859 -0.859375,-0.21484 -1.269531,-0.38672 -0.410157,-0.16797 -0.800782,-0.3789 -1.167969,-0.625 -0.367188,-0.24609 -0.710938,-0.52734 -1.023438,-0.83984 -0.316406,-0.3125 -0.59375,-0.65625 -0.839843,-1.02344 -0.25,-0.37109 -0.457032,-0.75781 -0.625,-1.16797 -0.171876,-0.41015 -0.300782,-0.83203 -0.386719,-1.26953 -0.08594,-0.43359 -0.132813,-0.875 -0.132813,-1.31641 V 212.96094 c 0,-0.44532 0.04687,-0.88282 0.132813,-1.32032 0.08594,-0.43359 0.214843,-0.85546 0.386719,-1.26562 0.167968,-0.41016 0.375,-0.80078 0.625,-1.16797 0.246093,-0.37109 0.523437,-0.71094 0.839843,-1.02344 0.3125,-0.3164 0.65625,-0.59765 1.023438,-0.84375 0.367187,-0.24609 0.757812,-0.45312 1.167969,-0.625 0.410156,-0.16797 0.832031,-0.29687 1.269531,-0.38281 0.433593,-0.0898 0.875,-0.13281 1.316406,-0.13281 H 141.22656 c 0.44531,0 0.88281,0.043 1.31641,0.13281 0.4375,0.0859 0.85937,0.21484 1.26953,0.38281 0.41016,0.17188 0.80078,0.37891 1.16797,0.625 0.37109,0.2461 0.71094,0.52735 1.02344,0.84375 0.3164,0.3125 0.59375,0.65235 0.84375,1.02344 0.24609,0.36719 0.45312,0.75781 0.625,1.16797 0.16796,0.41016 0.29687,0.83203 0.38281,1.26562 0.0898,0.4375 0.13281,0.875 0.13281,1.32032 z m 0,0"
                            fillOpacity="1"
                            fillRule="nonzero"
                            id="path12"
                          />
                        </g>
                      </svg>
                    </div>
                  </a>
                </div>
                <div className="footer-schon-genial">
                  <a
                    href={'https://www.schongenial.at/'}
                    target="_blank"
                    title="Schon genial"
                    rel="noreferrer"
                  >
                    <svg
                      viewBox="0 0 170 170"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        className="fill-path"
                        d="M104.86,57.64l-.08.39c-.47,1.65-1.09,3.4-1.81,4.99-1.62,3.42-3.94,6.97-6.85,10.13-2.08,2.39-4.55,4.7-6.91,6.5-1.26.95-2.69,1.46-4.15,1.43-1.46-.03-2.84-.44-4.11-1.37-2.29-1.8-4.76-4.05-6.92-6.5-2.92-3.27-5.16-6.68-6.85-10.1-.66-1.34-1.29-2.81-1.67-4.23-.43-1.16-.58-2.39-.6-3.61-.05-5.64,4.06-10.16,9.12-10.2,1.74-.05,3.47.57,4.87,1.53.23.18.59.39.81.57,1.45,1.37,3.44,2.04,5.31,2.02,1.87-.02,3.61-.74,5.05-1.92,1.64-1.54,3.69-2.46,5.94-2.41,5.06-.04,9.05,4.52,9.1,10.16.1.82.04,1.75-.27,2.62M137.75,107.74l.12-63.35c.06-1.59-.16-3.12-.41-4.5-.02-.54-.2-.98-.22-1.52-.12-.7-.38-1.42-.5-2.11-.25-.72-.38-1.42-.63-2.14-.18-.44-.35-.87-.53-1.31-4.6-10.72-15.3-18.25-27.8-18.25-5.44-.03-10.67,1.45-15.33,4.12-.31.21-.62.41-.92.61-.46.31-1.05.59-1.54,1.03-.92.62-1.72,1.26-2.54,2.03-.33.33-.8.64-1.16,1.1-.33.33-.69.8-1.03,1.13-.64-.8-1.43-1.5-2.21-2.19-.79-.7-1.58-1.4-2.52-1.99-.46-.36-1.04-.75-1.53-.98-.36-.2-.59-.39-.94-.59-4.64-2.69-9.8-4.14-15.27-4.04-9.71.03-18.5,4.67-24.06,11.72-3.94,4.96-6.29,11.32-6.22,18.16-.07,8.83,2.21,17.87,6.8,26.71.86,1.65,1.72,3.3,2.74,4.85,1.77,3.04,3.83,6,6.17,8.89,1.9,2.4,3.97,4.69,6.02,6.99,1.32,1.34,2.52,2.66,3.87,3.87.66.67,1.35,1.21,2.01,1.88,3.36,3.1,7.05,5.86,10.44,8.15,4.82,3.12,8.49,4.67,11.02,5.32l3.19.65c1.33,0,5.32.01,14.39-6.05,1.23-.82,2.46-1.64,3.72-2.59l-.05.93c-.07,14.05-4.3,23.11-21.99,23.13-10.25.06-17.69-4.54-20.36-8.43l-25.64,15.55c7.81,13.92,27.4,21.52,47.62,21.48,34.97.01,55.25-19.31,55.29-48.24"
                      />
                      <path
                        className="fill-text"
                        d="M102.75,58.39l5.76,28.54,9.81-2.02-5.76-28.54c-1.41.82-2.97,1.46-4.72,1.84-1.79.34-3.49.41-5.09.19M71.04,65.62l5.61,27.83,9.81-2.02-3.01-14.98c-.45-2.21.74-4.08,3.16-4.61,2.38-.49,4.24.79,4.68,3l3.01,14.98,9.81-2.02-3.49-17.38c-.82-4.05-3.72-10.79-12.38-8.99-3.75.79-5.76,2.88-6.77,5.17l-.59-2.96-9.85,1.99ZM51.08,81.46c.19-2.73,1.64-4.79,4.16-5.32,2.9-.6,4.91.71,5.98,3.18l-10.15,2.14ZM42.05,86.14c1.75,8.65,9.26,13.26,18.55,11.35,5.39-1.12,9.22-4.57,10.48-8.43l-7.4-2.58c-.67,1.24-2.19,2.85-4.65,3.37-3.2.67-5.58-.45-6.88-2.92l19.11-3.97c-1-11.5-9.44-16.07-17.58-14.38-8.44,1.65-13.27,9.36-11.63,17.57M29.15,85.88v.11c-.04.49-.11,1.01-.22,1.5-.26,1.05-.71,2.17-1.34,3.22-.45.79-1,1.57-1.56,2.21-.3.34-.67.56-1.08.64-.41.07-.82.04-1.23-.15-.74-.37-1.56-.86-2.3-1.42-1-.75-1.82-1.57-2.49-2.43-.26-.34-.52-.71-.71-1.09-.19-.3-.3-.64-.37-.97-.34-1.57.56-3.07,1.97-3.37.48-.11,1-.04,1.45.15.07.04.19.07.26.11.48.3,1.08.37,1.6.26.52-.11.97-.41,1.3-.82.37-.52.89-.9,1.52-1.01,1.41-.3,2.79.75,3.12,2.32.07.22.11.49.07.75M41.19,98.02l-3.57-17.72c-.07-.45-.22-.86-.37-1.24-.04-.15-.11-.26-.15-.41-.07-.19-.19-.37-.26-.56-.11-.19-.19-.37-.3-.56-.07-.11-.15-.22-.22-.34-1.9-2.73-5.32-4.23-8.81-3.52-1.52.3-2.9,1.01-4.05,2.02-.07.07-.15.15-.22.22-.11.11-.26.22-.37.37-.22.22-.41.45-.6.71-.07.11-.19.22-.26.37-.07.11-.15.26-.22.37-.22-.19-.48-.34-.74-.49-.26-.15-.52-.3-.82-.41-.15-.07-.33-.15-.48-.19-.11-.04-.19-.07-.3-.11-1.45-.49-2.97-.6-4.5-.26-2.71.56-4.91,2.36-6.06,4.64-.82,1.61-1.11,3.52-.71,5.43.48,2.47,1.64,4.87,3.42,7.08.33.41.67.82,1.04,1.2.67.75,1.41,1.46,2.23,2.14.67.56,1.38,1.09,2.08,1.61.45.3.86.6,1.3.86.22.15.45.26.67.41,1.11.67,2.3,1.24,3.38,1.69,1.52.6,2.64.82,3.38.86h.93c.37-.07,1.49-.3,3.68-2.51.3-.3.59-.6.89-.94l.04.26c.78,3.93.11,6.7-4.83,7.72-2.86.6-5.2-.26-6.17-1.2l-6.28,5.81c2.97,3.45,8.88,4.46,14.53,3.3,9.78-1.99,14.35-8.54,12.71-16.63M44.69,53.41c-.19-.86.48-1.5,1.64-1.72,1.08-.22,1.82.19,2.23.97l4.91-2.32c-1.41-2.92-4.98-3.78-8.1-3.15-4.53.94-7.62,3.86-6.84,7.68.71,3.56,3.16,4.38,6.84,4.46,2.19.04,3.75-.22,3.98.9.15.75-.37,1.42-1.6,1.69-1.6.34-2.38-.37-2.71-1.01l-5.28,2.92c1.41,3.07,5.35,3.41,9,2.66,5.32-1.09,8.03-4.01,7.25-7.87-.67-3.37-3.09-4.23-7.88-4.27-1.49,0-3.2.19-3.42-.94M72.97,46.03c-2.19-2.32-5.54-3.37-9.18-2.62-5.65,1.16-8.92,5.99-7.81,11.57,1.11,5.58,6.02,8.76,11.67,7.6,3.64-.75,6.32-3.07,7.43-6.07l-6.24-2.25c-.37,1.01-1.26,1.72-2.45,1.98-1.86.37-3.46-.56-3.87-2.58-.41-2.02.71-3.56,2.56-3.93,1.23-.26,2.34.07,3.05.86l4.83-4.57ZM95.31,56.44l-2.3-11.42c-.52-2.66-2.49-7.08-8.14-5.92-2.49.52-3.83,1.91-4.5,3.41l-1.79-8.91-6.43,1.31,5.09,25.25,6.43-1.31-1.97-9.85c-.3-1.46.48-2.7,2.08-3.03,1.56-.34,2.79.52,3.08,1.95l1.97,9.85,6.47-1.31ZM105.76,48.16c-1.86.37-3.49-.56-3.9-2.58-.41-2.02.74-3.56,2.6-3.93,1.86-.37,3.46.6,3.87,2.62.41,2.02-.71,3.52-2.56,3.9M114.87,42.92c-1.12-5.58-6.02-8.77-11.67-7.6-5.65,1.16-8.92,5.99-7.81,11.57,1.12,5.58,6.02,8.76,11.67,7.6,5.65-1.12,8.92-5.96,7.81-11.57M130.26,49.29l6.43-1.31-2.3-11.42c-.52-2.66-2.45-7.08-8.14-5.92-2.45.52-3.79,1.91-4.46,3.41l-.41-1.95-6.43,1.31,3.68,18.28,6.43-1.31-1.97-9.85c-.3-1.46.48-2.7,2.08-3.03,1.56-.34,2.79.52,3.08,1.95l2.01,9.85ZM135.98,72.14c-.48,1.2-1.71,2.4-3.57,2.77-1.75.37-2.94-.41-3.2-1.76-.37-1.76,1.08-3.15,5.43-4.05.26-.04.56-.11.71-.15l.63,3.18ZM147.61,78.88l-3.49-17.27c-1.34-6.63-5.84-10.45-14.68-8.65-5.35,1.09-9.22,3.78-11.56,7.57l7.14,3c.93-1.09,2.71-2.21,4.57-2.58,2.49-.52,4.27.6,4.8,3.18-.56.11-1.97.41-3.2.67-1.86.37-13.38,2.73-11.52,12.06,1.38,6.74,7.47,6.97,10.89,6.29,2.71-.56,5.69-2.36,6.69-4.79l.52,2.58,9.85-2.06ZM162,75.96l-7.77-38.43-9.81,2.02,7.77,38.43,9.81-2.02Z"
                      />
                      <path
                        className="fill-text"
                        d="M38.88,136.66c0,3.73-2.62,6.2-6.92,6.2h-2.52v5.58h-3.5v-18.01h6.02c4.3,0,6.92,2.5,6.92,6.23ZM35.33,136.66c0-1.9-1.23-2.98-3.37-2.98h-2.52v5.94h2.52c2.14,0,3.37-1.08,3.37-2.96Z"
                      />
                      <path
                        className="fill-text"
                        d="M52.92,144.87h-7.41l-1.21,3.58h-3.7l6.48-18.01h4.27l6.48,18.01h-3.7l-1.21-3.58ZM51.86,141.76l-2.65-7.8-2.65,7.8h5.3Z"
                      />
                      <path
                        className="fill-text"
                        d="M72.08,148.44l-3.7-6.48h-3.04v6.48h-3.47v-18.01h6.23c4.04,0,6.71,2.47,6.71,5.89,0,2.26-1.21,4.01-3.16,4.91l4.42,7.2h-3.99ZM65.34,138.95h2.75c1.8,0,3.14-.82,3.14-2.62,0-1.62-1.18-2.65-3.14-2.65h-2.75v5.27Z"
                      />
                      <path
                        className="fill-text"
                        d="M91.21,133.68h-4.32v14.77h-3.5v-14.77h-4.32v-3.24h12.14v3.24Z"
                      />
                      <path
                        className="fill-text"
                        d="M110.4,130.44v18.01h-3.16l-7.98-11.91v11.91h-3.5v-18.01h3.17l7.97,11.91v-11.91h3.5Z"
                      />
                      <path
                        className="fill-text"
                        d="M126.35,145.2v3.24h-10.62v-18.01h10.39v3.24h-6.92v4.06h6.33v3.24h-6.33v4.22h7.15Z"
                      />
                      <path
                        className="fill-text"
                        d="M141.37,148.44l-3.71-6.48h-3.04v6.48h-3.47v-18.01h6.23c4.04,0,6.71,2.47,6.71,5.89,0,2.26-1.21,4.01-3.16,4.91l4.42,7.2h-3.99ZM134.63,138.95h2.75c1.8,0,3.14-.82,3.14-2.62,0-1.62-1.18-2.65-3.14-2.65h-2.75v5.27Z"
                      />
                    </svg>
                  </a>
                </div>
              </div>
              <div className="footer-column footer-column-end adress">
                <a href={contact}>
                  <h3>
                    <FormattedMessage
                      id="find_us"
                      defaultMessage="Find us here"
                    />
                  </h3>
                </a>
                <div className="find-us">
                  <div className="contact-details">
                    <div className="companies">
                      <p>X-Net Services GmbH</p>
                      <p>X-Net Technologies GmbH</p>
                      <p>X-Net Holding GmbH</p>
                    </div>
                    <div className="adress-details">
                      <p>Spittelwiese 15</p>
                      <p>4020 Linz</p>
                      <p>
                        <FormattedMessage id="phone" defaultMessage="Phone" />:{' '}
                        <a href="tel:+437327731420">
                          +43&nbsp;732&nbsp;77&nbsp;31&nbsp;42 - 0
                        </a>
                      </p>
                      <p>
                        <FormattedMessage id="email" defaultMessage="Email" />:{' '}
                        <a href="mailto:office@x-net.at">office@x-net.at</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="footer-svg">
          <a href="/">
            <svg
              width="358"
              height="91"
              viewBox="0 0 358 91"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_193_46)">
                <path
                  d="M142.88 11.7253C143.764 17.9331 143.824 24.1477 143.811 30.3754C143.77 49.7559 143.797 69.1364 143.797 88.5169C143.797 90.1635 142.972 90.9868 141.321 90.9868C138.557 90.9868 135.8 90.9868 133.037 90.9868C130.855 90.9868 130.801 90.9204 130.788 88.6895C130.788 88.4903 130.788 88.2912 130.788 88.092C130.788 59.642 130.788 31.1921 130.788 2.74878C130.788 2.15123 130.788 1.55368 130.835 0.956133C130.875 0.371863 131.21 0.0531705 131.819 0.0531705C132.12 0.0531705 132.421 0.0332522 132.722 0.0332522C136.436 0.0332522 140.157 0.05981 143.871 0.0266128C147.137 -0.00658432 149.499 1.3545 151.152 4.17625C160.654 20.3565 170.197 36.5235 179.746 52.6773C182.791 57.8361 185.783 63.0215 188.941 68.1139C190.761 71.0486 192.127 74.2156 193.432 77.4025C193.632 77.8872 193.86 78.3652 194.308 78.7769C194.188 77.7544 194.094 76.7253 193.954 75.7028C193.338 71.2544 193.184 66.7728 193.184 62.2912C193.171 42.4592 193.184 22.6339 193.177 2.80189C193.177 -0.491263 193.452 0.05981 195.995 0.0398917C198.758 0.013334 201.515 0.0266128 204.279 0.0398917C206.14 0.0398917 206.18 0.0930072 206.193 1.99852C206.193 2.2973 206.193 2.59607 206.193 2.89485C206.193 31.3448 206.193 59.7947 206.193 88.238C206.193 88.6895 206.207 89.1344 206.193 89.5858C206.146 90.7809 205.999 90.9669 204.754 90.9735C200.84 91 196.918 91.0266 193.003 90.9668C189.851 90.9137 187.543 89.546 185.91 86.7442C176.247 70.1921 166.516 53.6798 156.78 37.1676C154.09 32.6129 151.486 28.0052 148.642 23.5435C146.648 20.4229 145.102 17.0634 143.684 13.6507C143.416 13.0133 143.162 12.3693 142.901 11.7253H142.88Z"
                  fill="#FFD7C8"
                />
                <path
                  d="M222.608 42.8311C222.608 29.4327 222.608 16.0344 222.608 2.63601C222.608 1.09566 223.38 0.323274 224.924 0.318848C242.095 0.318848 259.26 0.318848 276.432 0.318848C277.984 0.318848 278.763 1.08681 278.767 2.62273C278.767 4.06349 278.828 5.50424 278.346 6.9118C277.342 9.84643 275.247 11.5196 272.136 11.7586C271.587 11.7984 271.031 11.7851 270.483 11.7851C259.842 11.7851 249.196 11.7851 238.555 11.7851C237.079 11.7851 236.34 12.531 236.34 14.0226C236.34 21.492 236.34 28.968 236.34 36.4373C236.34 36.8357 236.354 37.2341 236.354 37.6324C236.354 38.3229 236.715 38.6217 237.384 38.6084C237.833 38.6084 238.288 38.6151 238.743 38.6151C250.795 38.6151 262.84 38.6151 274.893 38.6151C277.288 38.6151 277.282 38.6151 277.302 40.9986C277.315 42.5456 277.302 44.0793 276.726 45.5599C275.823 47.8837 274.19 49.3245 271.727 49.849C270.637 50.0814 269.532 50.088 268.428 50.088C258.437 50.088 248.446 50.088 238.455 50.088C237.054 50.088 236.351 50.7652 236.347 52.1197C236.347 60.3857 236.347 68.6585 236.347 76.9246C236.347 77.0773 236.347 77.2233 236.347 77.376C236.374 78.9562 236.441 79.0425 238.087 79.0425C247.523 79.0492 256.965 79.0425 266.401 79.0425C270.115 79.0425 273.829 79.0425 277.543 79.0425C277.944 79.0425 278.346 79.0492 278.747 79.0625C279.457 79.0824 279.872 79.421 279.898 80.158C279.905 80.3572 279.925 80.5563 279.925 80.7555C279.925 83.4976 279.945 86.233 279.925 88.9751C279.912 90.4424 279.724 90.6084 278.286 90.6616C277.984 90.6748 277.683 90.6616 277.382 90.6616C262.071 90.6616 246.76 90.6616 231.448 90.6616C230.391 90.6616 229.341 90.5952 228.31 90.3628C224.736 89.5727 222.675 87.0829 222.642 83.4578C222.608 80.1181 222.635 76.7851 222.635 73.4455C222.635 63.2341 222.635 53.0226 222.635 42.8112L222.608 42.8311Z"
                  fill="#FFD7C8"
                />
                <path
                  d="M329.118 50.9313C329.118 63.3802 329.118 75.8358 329.118 88.2847C329.118 90.6682 329.118 90.6815 326.682 90.6682C324.975 90.6616 323.269 90.7745 321.576 90.4823C317.554 89.7852 315.412 87.3153 315.412 83.252C315.406 60.1933 315.412 37.1345 315.406 14.0758C315.406 11.3603 315.673 11.5064 312.782 11.5064C305.401 11.5064 298.027 11.5064 290.645 11.5064C288.444 11.5064 288.384 11.4466 288.377 9.22903C288.377 6.68613 288.357 4.14987 288.384 1.60697C288.397 0.199407 288.551 0.0732574 289.943 0.0400602C290.244 0.0334208 290.545 0.0400602 290.846 0.0400602C312.381 0.0400602 333.916 0.0400602 355.45 0.0400602C357.993 0.0400602 358 0.0400603 357.987 2.5896C357.987 3.73158 358.06 4.87356 357.793 6.00891C356.969 9.49461 354.5 11.4997 350.913 11.5064C344.435 11.5196 337.964 11.5064 331.486 11.5064C331.085 11.5064 330.683 11.4931 330.282 11.5196C329.305 11.586 329.178 11.7122 329.124 12.6815C329.097 13.1264 329.118 13.5779 329.118 14.0227C329.118 26.3256 329.118 38.6284 329.118 50.9313Z"
                  fill="#FFD7C8"
                />
                <path
                  d="M53.7902 32.8784L29.0834 0.537794C28.8291 0.199183 28.4276 0 27.9993 0H10.0046C9.57635 0 9.17483 0.199183 8.91384 0.537794L0.274516 11.9709C-0.0935417 12.4556 -0.0868497 13.1195 0.274516 13.5975L24.747 45.4469L0.32136 77.4091C-0.0466979 77.8871 -0.0466979 78.5511 0.32136 79.0357L8.9473 90.429C9.2016 90.7676 9.60981 90.9668 10.0381 90.9668H28.0194C28.4477 90.9668 28.8492 90.7676 29.1035 90.4356L53.7969 58.1747L78.4902 90.4356C78.7445 90.7743 79.146 90.9668 79.5743 90.9668H97.5556C97.9839 90.9668 98.3854 90.7676 98.6464 90.429L107.272 79.0357C107.64 78.5511 107.634 77.8871 107.272 77.4091L82.84 45.4535L107.313 13.6042C107.681 13.1261 107.687 12.4556 107.313 11.9775L98.6665 0.537794C98.4122 0.199183 98.004 0 97.5757 0H79.581C79.1527 0 78.7512 0.199183 78.4969 0.537794L53.7902 32.8784Z"
                  fill="#FFD7C8"
                />
              </g>
              <defs>
                <clipPath id="clip0_193_46">
                  <rect width="358" height="91" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </a>
        </div>
        <div className="footer-end">
          <a href={imprintHref}>
            <FormattedMessage id="imprint" defaultMessage="Imprint" />
          </a>
          <a href={'/de/datenschutz'}>
            <FormattedMessage id="data_privacy" defaultMessage="Data privacy" />
          </a>
          <a href={vdpHref}>
            <FormattedMessage id="vdp" defaultMessage="VDP" />
          </a>
        </div>
      </div>
    </Segment>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
Footer.propTypes = {
  /**
   * i18n object
   */
};

export default injectIntl(Footer);
